import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { EmptyState, LoadingShell } from '@/components/ui-bits';
import { fmtRelative } from '@/lib/format';
import { Plus, Search, Users, Phone, Mail, Upload, X, Download, Trash2, Send, FileText, MessageCircle, UserPlus, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { listVisibleSystemFields, listSystemFieldsInTable, resolveSysField } from '@/lib/fields';
import { useAuth } from '@/lib/auth-context';
import { canWrite, canSupervise } from '@/lib/roles';
import { useGlossary } from '@/lib/glossary';

const PHONE_PLACEHOLDER = '4771234567';

function renderSysInput(f, value, onChange) {
  if (f.type === 'phone') return <input type="tel" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder || PHONE_PLACEHOLDER} className="input" required={f.required} />;
  if (f.type === 'email') return <input type="email" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} className="input" required={f.required} />;
  return <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} className="input" required={f.required} />;
}

function LeadCreateModal({ open, onClose, onCreated, customFields, users, sysFields }) {
  const [f, setF] = useState({ first_name: '', last_name: '', phone: '', email: '', city: '', source: '', service: '', custom_values: {}, auto_assign: true });
  const [loading, setLoading] = useState(false);
  const sysKeyToFormKey = { phone_e164: 'phone' };
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post('/leads', f);
      toast.success('Lead creado', { description: `${data.lead.first_name} ${data.lead.last_name}` });
      onCreated(data.lead);
      onClose();
    } catch (err) {
      toast.error('No se pudo crear', { description: err?.response?.data?.detail || 'Error' });
    } finally { setLoading(false); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="lead-create-modal" className="surface w-full max-w-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold">Nuevo lead</h2>
          <button type="button" onClick={onClose} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {sysFields.map((sf) => {
            const formKey = sysKeyToFormKey[sf.key] || sf.key;
            return (
              <label key={sf.key} data-testid={`lead-${formKey}`} className="block">
                <span className="text-xs font-medium text-muted-foreground">{sf.label}{sf.required ? ' *' : ''}</span>
                <div className="mt-1">{renderSysInput(sf, f[formKey], (v) => setF({ ...f, [formKey]: v }))}</div>
              </label>
            );
          })}
          <label className="block" data-testid="lead-assignee">
            <span className="text-xs font-medium text-muted-foreground">Asignar a</span>
            <select value={f.assigned_to || ''} onChange={(e) => setF({ ...f, assigned_to: e.target.value || null, auto_assign: !e.target.value })} className="input mt-1">
              <option value="">Auto-asignar (reglas)</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </label>
        </div>
        {customFields.length > 0 && (
          <div className="mt-4 border-t border-border pt-4">
            <h3 className="text-sm font-medium mb-2">Campos personalizados</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {customFields.filter((c) => !c.hidden).map((cf) => (
                <label key={cf.id} data-testid={`cf-${cf.key}`} className="block">
                  <span className="text-xs font-medium text-muted-foreground">{cf.label}{cf.required ? ' *' : ''}</span>
                  <div className="mt-1"><CustomFieldInput cf={cf} value={f.custom_values?.[cf.key]} onChange={(v) => setF({ ...f, custom_values: { ...(f.custom_values || {}), [cf.key]: v } })} /></div>
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
          <button data-testid="lead-create-submit" disabled={loading} className="btn-primary">{loading ? 'Creando…' : 'Crear lead'}</button>
        </div>
      </form>
    </div>
  );
}

export function CustomFieldInput({ cf, value, onChange }) {
  if (cf.type === 'textarea') return <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} className="input min-h-[72px]" />;
  if (cf.type === 'select' || cf.type === 'radio') return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="input">
      <option value="">— Selecciona —</option>
      {(cf.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (cf.type === 'multiselect') return (
    <select multiple value={value || []} onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))} className="input min-h-[80px]">
      {(cf.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (cf.type === 'checkbox' || cf.type === 'boolean') return <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />;
  if (cf.type === 'date') return <input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className="input" />;
  if (cf.type === 'datetime') return <input type="datetime-local" value={value || ''} onChange={(e) => onChange(e.target.value)} className="input" />;
  if (cf.type === 'number' || cf.type === 'currency') return <input type="number" value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))} className="input" />;
  return <input type={cf.type === 'email' ? 'email' : cf.type === 'phone' ? 'tel' : 'text'} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={cf.placeholder || ''} className="input" />;
}

function formatCustomVal(v, type) {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ');
  if (v === true) return 'Sí';
  if (v === false) return 'No';
  if (type === 'currency' && typeof v === 'number') return `$${v.toLocaleString('es-MX')}`;
  return String(v);
}

// ---- Bulk / Scheduled WhatsApp Modal ----
const INTERVAL_OPTIONS = [
  { seconds: 15, label: '15 segundos' },
  { seconds: 30, label: '30 segundos' },
  { seconds: 60, label: '1 minuto' },
  { seconds: 300, label: '5 minutos' },
  { seconds: 900, label: '15 minutos' },
  { seconds: 3600, label: '1 hora' },
];

function BulkWhatsAppModal({ open, onClose, leadIds, templates, onSent }) {
  const [templateId, setTemplateId] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [intervalSec, setIntervalSec] = useState(15);
  const [mode, setMode] = useState('now'); // now | schedule
  const [scheduledLocal, setScheduledLocal] = useState('');
  const [name, setName] = useState('');

  if (!open) return null;

  const pickTemplate = (id) => {
    setTemplateId(id);
    if (!id) { setBody(''); return; }
    const t = templates.find((x) => x.id === id);
    if (t) setBody(t.body || '');
  };

  const toIsoLocal = (localValue) => {
    if (!localValue) return null;
    const d = new Date(localValue);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const send = async () => {
    const tpl = templates.find((x) => x.id === templateId);
    if (!body.trim() && !tpl?.media_filename) {
      toast.error('Falta el mensaje (o una plantilla con imagen)');
      return;
    }
    setSending(true);
    try {
      const payload = {
        lead_ids: leadIds,
        body,
        template_id: templateId || null,
        interval_seconds: Number(intervalSec) || 15,
        name: name || undefined,
      };
      if (mode === 'schedule') {
        const iso = toIsoLocal(scheduledLocal);
        if (!iso) {
          toast.error('Indica fecha y hora de inicio');
          setSending(false);
          return;
        }
        payload.scheduled_at = iso;
      }
      // Include template media if present
      if (tpl?.media_filename) {
        payload.media_type = tpl.media_type || 'image';
        payload.media_filename = tpl.media_filename;
        payload.media_mimetype = tpl.media_mimetype;
      }
      const { data } = await api.post('/whatsapp/campaigns', payload);
      const c = data.campaign;
      const intervalLabel = INTERVAL_OPTIONS.find((i) => i.seconds === c.interval_seconds)?.label || `${c.interval_seconds}s`;
      toast.success(c.status === 'scheduled' ? 'Campaña programada' : 'Campaña iniciada', {
        description: `${c.stats?.total || leadIds.length} leads · intervalo ${intervalLabel}. Corre en el servidor aunque cierres el CRM.`,
      });
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-2xl p-6 shadow-lg" data-testid="bulk-whatsapp-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            WhatsApp masivo / programado · {leadIds.length} lead{leadIds.length === 1 ? '' : 's'}
          </h2>
          <button onClick={onClose} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Nombre de la campaña (opcional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="input mt-1" placeholder="Ej. Seguimiento marzo" />
        </label>

        <label className="block mt-3">
          <span className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Plantilla (opcional, editable antes de enviar)
          </span>
          <select data-testid="bulk-whatsapp-template" value={templateId} onChange={(e) => pickTemplate(e.target.value)} className="input mt-1">
            <option value="">— Sin plantilla (escribir libre) —</option>
            {templates.filter((t) => t.enabled !== false).map((t) => (
              <option key={t.id} value={t.id}>{t.name}{t.media_filename ? ' 📷' : ''}</option>
            ))}
          </select>
        </label>

        <label className="block mt-3">
          <span className="text-xs font-medium text-muted-foreground">
            Mensaje (variables: (nombre_cliente), (nombre_empleado), (nombre_empresa)…)
          </span>
          <textarea data-testid="bulk-whatsapp-body" value={body} onChange={(e) => setBody(e.target.value)} className="input mt-1 min-h-[140px]" />
        </label>

        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Intervalo entre mensajes</span>
            <select
              data-testid="bulk-whatsapp-interval"
              value={intervalSec}
              onChange={(e) => setIntervalSec(Number(e.target.value))}
              className="input mt-1"
            >
              {INTERVAL_OPTIONS.map((o) => (
                <option key={o.seconds} value={o.seconds}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Cuándo enviar</span>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="input mt-1">
              <option value="now">Iniciar ahora (en segundo plano)</option>
              <option value="schedule">Programar fecha y hora</option>
            </select>
          </label>
        </div>

        {mode === 'schedule' && (
          <label className="block mt-3">
            <span className="text-xs font-medium text-muted-foreground">Inicio programado</span>
            <input
              type="datetime-local"
              value={scheduledLocal}
              onChange={(e) => setScheduledLocal(e.target.value)}
              className="input mt-1"
              data-testid="bulk-whatsapp-schedule"
            />
          </label>
        )}

        <div className="mt-3 rounded-xl border border-border bg-secondary/30 p-3 text-xs text-muted-foreground space-y-1">
          <p>
            El envío corre en el <strong>servidor</strong> (worker). Puedes cerrar el CRM y seguirá enviando con el intervalo elegido.
          </p>
          <p>
            Con {leadIds.length} leads e intervalo de {INTERVAL_OPTIONS.find((i) => i.seconds === Number(intervalSec))?.label || `${intervalSec}s`},
            el total estimado es ~{Math.ceil((Math.max(leadIds.length - 1, 0) * Number(intervalSec)) / 60)} min.
          </p>
        </div>

        <div className="flex items-center justify-between mt-4">
          <p className="text-[11px] text-muted-foreground">
            Cada mensaje se personaliza por lead. Sin teléfono = saltado. Ver progreso en Campañas.
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost">Cancelar</button>
            <button
              data-testid="bulk-whatsapp-send"
              disabled={sending || (!body.trim() && !templates.find((t) => t.id === templateId)?.media_filename)}
              onClick={send}
              className="btn-primary"
            >
              <MessageCircle className="h-4 w-4" />
              {sending ? 'Guardando…' : mode === 'schedule' ? `Programar (${leadIds.length})` : `Iniciar envío (${leadIds.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Bulk / Scheduled Email Modal ----
function BulkEmailModal({ open, onClose, leadIds, emailTemplates, onSent }) {
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [intervalSec, setIntervalSec] = useState(60);
  const [mode, setMode] = useState('now');
  const [scheduledLocal, setScheduledLocal] = useState('');
  const [name, setName] = useState('');

  if (!open) return null;

  const pickTemplate = (id) => {
    setTemplateId(id);
    if (!id) { setSubject(''); setBody(''); return; }
    const t = emailTemplates.find((x) => x.id === id);
    if (t) { setSubject(t.subject || ''); setBody(t.body_html || ''); }
  };

  const toIsoLocal = (localValue) => {
    if (!localValue) return null;
    const d = new Date(localValue);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Falta asunto o cuerpo');
      return;
    }
    setSending(true);
    try {
      const payload = {
        channel: 'email',
        lead_ids: leadIds,
        subject,
        body,
        template_id: templateId || null,
        interval_seconds: Number(intervalSec) || 60,
        name: name || undefined,
      };
      if (mode === 'schedule') {
        const iso = toIsoLocal(scheduledLocal);
        if (!iso) {
          toast.error('Indica fecha y hora de inicio');
          setSending(false);
          return;
        }
        payload.scheduled_at = iso;
      }
      const { data } = await api.post('/whatsapp/campaigns', payload);
      const c = data.campaign;
      toast.success(c.status === 'scheduled' ? 'Campaña email programada' : 'Campaña email iniciada', {
        description: `${c.stats?.total || leadIds.length} leads · intervalo ${intervalSec}s. Corre en el servidor.`,
      });
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-2xl p-6 shadow-lg" data-testid="bulk-email-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <Send className="h-4 w-4" />
            Email masivo / programado · {leadIds.length} lead{leadIds.length === 1 ? '' : 's'}
          </h2>
          <button onClick={onClose} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Nombre (opcional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="input mt-1" placeholder="Ej. Newsletter marzo" />
        </label>

        <label className="block mt-3">
          <span className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Plantilla (opcional, editable antes de enviar)
          </span>
          <select data-testid="bulk-email-template" value={templateId} onChange={(e) => pickTemplate(e.target.value)} className="input mt-1">
            <option value="">— Sin plantilla (escribir libre) —</option>
            {emailTemplates.filter((t) => t.enabled !== false).map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
          </select>
        </label>

        <label className="block mt-3">
          <span className="text-xs font-medium text-muted-foreground">Asunto</span>
          <input data-testid="bulk-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="input mt-1" placeholder="Asunto" />
        </label>

        <label className="block mt-3">
          <span className="text-xs font-medium text-muted-foreground">
            Cuerpo (HTML y variables: (nombre_cliente), (nombre_empleado)…)
          </span>
          <textarea data-testid="bulk-email-body" value={body} onChange={(e) => setBody(e.target.value)} className="input mt-1 min-h-[160px] font-mono text-xs" />
        </label>

        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Intervalo entre emails</span>
            <select value={intervalSec} onChange={(e) => setIntervalSec(Number(e.target.value))} className="input mt-1">
              {INTERVAL_OPTIONS.map((o) => (
                <option key={o.seconds} value={o.seconds}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Cuándo enviar</span>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="input mt-1">
              <option value="now">Iniciar ahora (servidor)</option>
              <option value="schedule">Programar fecha y hora</option>
            </select>
          </label>
        </div>
        {mode === 'schedule' && (
          <label className="block mt-3">
            <span className="text-xs font-medium text-muted-foreground">Inicio programado</span>
            <input type="datetime-local" value={scheduledLocal} onChange={(e) => setScheduledLocal(e.target.value)} className="input mt-1" />
          </label>
        )}

        <div className="mt-3 rounded-xl border border-border bg-secondary/30 p-3 text-xs text-muted-foreground">
          Corre en el servidor con el intervalo elegido. Leads sin email se saltan. Progreso en Campañas.
        </div>

        <div className="flex items-center justify-between mt-4">
          <p className="text-[11px] text-muted-foreground">SMTP del tenant o global requerido.</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost">Cancelar</button>
            <button data-testid="bulk-email-send" disabled={sending || !subject.trim() || !body.trim()} onClick={send} className="btn-primary">
              <Send className="h-4 w-4" />
              {sending ? 'Guardando…' : mode === 'schedule' ? `Programar (${leadIds.length})` : `Iniciar (${leadIds.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LeadsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useGlossary();
  const writable = canWrite(user?.role);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [stage, setStage] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [listId, setListId] = useState(searchParams.get('list') || '');
  const [leadLists, setLeadLists] = useState([]);
  const [activeList, setActiveList] = useState(null);
  const [scope, setScope] = useState(''); // '' = policy default, mine|team|all
  const [assigned, setAssigned] = useState('');
  const [status, setStatus] = useState('');
  const [tag, setTag] = useState('');
  const [source, setSource] = useState('');
  const [hasPhone, setHasPhone] = useState('');
  const [hasEmail, setHasEmail] = useState('');
  const [users, setUsers] = useState([]);
  const [stages, setStages] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [tags, setTags] = useState([]);
  const [views, setViews] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [systemFields, setSystemFields] = useState({});
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkWhatsAppOpen, setBulkWhatsAppOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [bulkStageTo, setBulkStageTo] = useState('');
  const [bulkListTo, setBulkListTo] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);
  const [total, setTotal] = useState(0);
  const [moreFilters, setMoreFilters] = useState(false);

  const tableSys = useMemo(() => {
    if (activeList?.columns?.system?.length) {
      return activeList.columns.system
        .map((key) => resolveSysField(key, systemFields))
        .filter(Boolean)
        .filter((f) => !f.hidden);
    }
    return listSystemFieldsInTable(systemFields);
  }, [systemFields, activeList]);
  const formSys = useMemo(() => listVisibleSystemFields(systemFields), [systemFields]);
  const tableCustom = useMemo(() => {
    if (activeList?.columns?.custom?.length) {
      const keys = new Set(activeList.columns.custom);
      return customFields.filter((c) => keys.has(c.key) && !c.hidden).sort((a, b) => a.order - b.order);
    }
    return customFields.filter((c) => c.show_in_list).sort((a, b) => a.order - b.order);
  }, [customFields, activeList]);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (stage) params.set('stage_id', stage);
    if (pipelineId) params.set('pipeline_id', pipelineId);
    if (listId) params.set('list_id', listId);
    if (scope) params.set('scope', scope);
    if (assigned) params.set('assigned_to', assigned);
    if (status) params.set('status', status);
    if (tag) params.set('tag', tag);
    if (source) params.set('source', source);
    if (hasPhone === '1') params.set('has_phone', 'true');
    if (hasPhone === '0') params.set('has_phone', 'false');
    if (hasEmail === '1') params.set('has_email', 'true');
    if (hasEmail === '0') params.set('has_email', 'false');
    params.set('limit', String(pageSize));
    params.set('offset', String((page - 1) * pageSize));
    const { data } = await api.get(`/leads?${params}`);
    setLeads(data.leads);
    setTotal(data.total || 0);
    // drop selections that are no longer present
    setSelected((prev) => {
      const next = new Set();
      const ids = new Set(data.leads.map((l) => l.id));
      prev.forEach((id) => { if (ids.has(id)) next.add(id); });
      return next;
    });
    setLoading(false);
  };

  useEffect(() => {
    Promise.all([
      api.get('/admin/users').then((r) => setUsers(r.data.users)).catch(() => {}),
      api.get('/pipeline/list').then((r) => {
        setPipelines(r.data.pipelines || []);
        const def = (r.data.pipelines || []).find((p) => p.is_default) || (r.data.pipelines || [])[0];
        if (def) setStages((def.stages || []).sort((a, b) => a.order - b.order));
      }).catch(() => {
        api.get('/pipeline').then((r) => setStages((r.data.pipeline.stages || []).sort((a, b) => a.order - b.order))).catch(() => {});
      }),
      api.get('/custom-fields').then((r) => setCustomFields(r.data.fields)).catch(() => {}),
      api.get('/admin/settings').then((r) => setSystemFields(r.data.tenant?.settings?.system_fields || {})).catch(() => {}),
      api.get('/email/templates').then((r) => setEmailTemplates(r.data.templates || [])).catch(() => {}),
      api.get('/whatsapp/templates').then((r) => setWaTemplates(r.data.templates || [])).catch(() => {}),
      api.get('/tags').then((r) => setTags(r.data.tags || [])).catch(() => {}),
      api.get('/views').then((r) => setViews(r.data.views || [])).catch(() => {}),
      api.get('/lead-lists?active_only=true').then((r) => setLeadLists(r.data.lists || [])).catch(() => {}),
    ]);
  }, []);

  useEffect(() => {
    const fromUrl = searchParams.get('list') || '';
    if (fromUrl !== listId) setListId(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!listId) {
      setActiveList(null);
      return;
    }
    const found = leadLists.find((l) => l.id === listId);
    if (found) setActiveList(found);
    else {
      api.get(`/lead-lists/${listId}`).then((r) => setActiveList(r.data.list)).catch(() => setActiveList(null));
    }
  }, [listId, leadLists]);

  useEffect(() => { setPage(1); }, [q, stage, assigned, status, tag, source, hasPhone, hasEmail, pageSize, listId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q, stage, pipelineId, listId, scope, assigned, status, tag, source, hasPhone, hasEmail, page, pageSize]);

  useEffect(() => {
    if (!pipelineId) {
      const def = pipelines.find((p) => p.is_default) || pipelines[0];
      if (def) setStages((def.stages || []).sort((a, b) => a.order - b.order));
      return;
    }
    const p = pipelines.find((x) => x.id === pipelineId);
    if (p) setStages((p.stages || []).sort((a, b) => a.order - b.order));
  }, [pipelineId, pipelines]);

  const applyView = (v) => {
    const c = v.config || {};
    setStage(c.stage_id || '');
    setAssigned(c.assigned_to || '');
    setStatus(c.status || '');
    setTag(c.tag || '');
    setSource(c.source || '');
    setHasPhone(c.has_phone === true ? '1' : c.has_phone === false ? '0' : '');
    setHasEmail(c.has_email === true ? '1' : c.has_email === false ? '0' : '');
    setQ(c.q || '');
    toast.success(`Vista: ${v.name}`);
  };

  const saveCurrentView = async () => {
    const name = prompt('Nombre de la vista');
    if (!name) return;
    try {
      await api.post('/views', {
        name,
        config: {
          stage_id: stage || undefined,
          assigned_to: assigned || undefined,
          status: status || undefined,
          tag: tag || undefined,
          source: source || undefined,
          has_phone: hasPhone === '1' ? true : hasPhone === '0' ? false : undefined,
          has_email: hasEmail === '1' ? true : hasEmail === '0' ? false : undefined,
          q: q || undefined,
        },
      });
      const { data } = await api.get('/views');
      setViews(data.views || []);
      toast.success('Vista guardada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    }
  };

  const stageMap = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, s])), [stages]);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const qs = listId ? `?list_id=${encodeURIComponent(listId)}` : '';
      const { data } = await api.post(`/leads/import${qs}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const extra = data.list_name
        ? ` · ${data.added_to_list ?? 0} en lista “${data.list_name}”`
        : '';
      toast.success(`Importados ${data.created}`, {
        description: `${data.duplicates} duplicados, ${data.invalid} inválidos${extra}`,
      });
      load();
      if (listId) {
        api.get('/lead-lists?active_only=true').then((r) => setLeadLists(r.data.lists || [])).catch(() => {});
      }
    } catch (err) { toast.error('Error al importar', { description: err?.response?.data?.detail || 'Error' }); }
    finally { setImporting(false); e.target.value = ''; }
  };

  const onExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const res = await api.get(`/leads/export?format=${format}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Exportación lista');
    } catch (err) {
      toast.error('Error al exportar', { description: err?.response?.data?.detail || 'Error' });
    } finally {
      setExporting(false);
    }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allOnPageSelected = leads.length > 0 && leads.every((l) => selected.has(l.id));
  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(leads.map((l) => l.id)));
    }
  };

  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`¿Eliminar ${selected.size} lead${selected.size === 1 ? '' : 's'} de forma permanente?\n\nSe borrarán también sus notas, tareas, mensajes y actividades. Esta acción no se puede deshacer.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post('/leads/bulk-delete', { lead_ids: [...selected] });
      toast.success(`${data.deleted} eliminado${data.deleted === 1 ? '' : 's'}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar');
    } finally {
      setBulkDeleting(false);
    }
  };

  const bulkAssign = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/leads/bulk-assign', {
        lead_ids: [...selected],
        assigned_to: bulkAssignTo || null,
      });
      toast.success(`${data.updated} asignado${data.updated === 1 ? '' : 's'}`);
      setBulkAssignTo('');
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo asignar');
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkStage = async () => {
    if (selected.size === 0 || !bulkStageTo) {
      toast.error('Elige una etapa');
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post('/leads/bulk-stage', {
        lead_ids: [...selected],
        stage_id: bulkStageTo,
      });
      toast.success(`${data.updated} movido${data.updated === 1 ? '' : 's'}`);
      setBulkStageTo('');
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo mover');
    } finally {
      setBulkBusy(false);
    }
  };

  const renderSysCell = (l, sf) => {
    let v = l[sf.key];
    if (sf.key === 'first_name') {
      return <span className="text-sm font-medium hover:underline">{l.first_name} {!resolveSysField('last_name', systemFields).hidden && l.last_name}</span>;
    }
    if (sf.key === 'last_name') {
      if (!resolveSysField('first_name', systemFields).hidden) return null;
      return <span className="text-sm">{v || '—'}</span>;
    }
    if (sf.key === 'phone_e164') return v ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{v}</span> : <span className="text-muted-foreground">—</span>;
    if (sf.key === 'email') return v ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" />{v}</span> : <span className="text-muted-foreground">—</span>;
    return <span className="text-xs text-muted-foreground">{v || '—'}</span>;
  };

  const tableSysFinal = useMemo(() => {
    const firstVisible = !resolveSysField('first_name', systemFields).hidden;
    return tableSys.filter((sf) => !(sf.key === 'last_name' && firstVisible));
  }, [tableSys, systemFields]);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full">
      <div className="shrink-0 px-4 pt-3 pb-2 border-b border-border bg-card/40 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h1 className="page-title truncate">{activeList ? activeList.name : t('leads')}</h1>
          <div className="flex items-center gap-1.5 shrink-0">
            <Link to="/app/lead-lists" className="btn-ghost h-8 text-xs">Listas</Link>
            <button data-testid="leads-export-button" onClick={() => onExport('csv')} disabled={exporting} className="btn-ghost h-8 w-8 p-0" title="Exportar">
              <Download className="h-4 w-4" />
            </button>
            {writable && (
              <label data-testid="leads-import-button" className="btn-ghost h-8 w-8 p-0 cursor-pointer grid place-items-center" title="Importar">
                <Upload className="h-4 w-4" />
                <input type="file" accept=".csv,.xlsx,.xls" onChange={onImport} className="hidden" />
              </label>
            )}
            {writable && (
              <button data-testid="leads-create-button" onClick={() => setOpen(true)} className="btn-primary h-8 text-xs">
                <Plus className="h-3.5 w-3.5" /> Nuevo
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="relative flex-1 min-w-[160px] max-w-xs">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="input pl-8 h-8 text-xs" />
          </div>
          <select
            value={listId}
            onChange={(e) => {
              const v = e.target.value;
              setListId(v);
              setPage(1);
              if (v) setSearchParams({ list: v });
              else setSearchParams({});
            }}
            className="input h-8 w-auto min-w-[140px] text-xs"
          >
            <option value="">Todas las listas</option>
            {leadLists.map((lst) => (
              <option key={lst.id} value={lst.id}>{lst.name}</option>
            ))}
          </select>
          <select data-testid="leads-stage-filter" value={stage} onChange={(e) => setStage(e.target.value)} className="input h-8 w-auto text-xs">
            <option value="">Etapa</option>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select data-testid="leads-assignee-filter" value={assigned} onChange={(e) => setAssigned(e.target.value)} className="input h-8 w-auto text-xs">
            <option value="">Agente</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
          <button type="button" className={`filter-pill ${moreFilters ? 'filter-pill-active' : ''}`} onClick={() => setMoreFilters((v) => !v)}>
            {moreFilters ? 'Menos' : 'Más filtros'}
          </button>
        </div>
        {moreFilters && (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            <select value={scope} onChange={(e) => { setScope(e.target.value); setPage(1); }} className="input h-8 w-auto text-xs">
              <option value="">Visibilidad</option>
              <option value="mine">Solo míos</option>
              <option value="team">Mi equipo</option>
              {canSupervise(user?.role) && <option value="all">Todos</option>}
            </select>
            {pipelines.length > 1 && (
              <select value={pipelineId} onChange={(e) => { setPipelineId(e.target.value); setStage(''); setPage(1); }} className="input h-8 w-auto text-xs">
                <option value="">Pipeline</option>
                {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="input h-8 w-auto text-xs">
              <option value="">Status</option>
              <option value="open">Abiertos</option>
              <option value="won">Ganados</option>
              <option value="lost">Perdidos</option>
              <option value="closed">Cerrados</option>
            </select>
            <select value={tag} onChange={(e) => setTag(e.target.value)} className="input h-8 w-auto text-xs">
              <option value="">Tag</option>
              {tags.map((tg) => <option key={tg.id || tg.name} value={tg.name}>{tg.name}</option>)}
            </select>
            <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Origen" className="input h-8 w-24 text-xs" />
            <select value={hasPhone} onChange={(e) => setHasPhone(e.target.value)} className="input h-8 w-auto text-xs">
              <option value="">Tel.</option>
              <option value="1">Con tel.</option>
              <option value="0">Sin tel.</option>
            </select>
            <select value={hasEmail} onChange={(e) => setHasEmail(e.target.value)} className="input h-8 w-auto text-xs">
              <option value="">Email</option>
              <option value="1">Con email</option>
              <option value="0">Sin email</option>
            </select>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="input h-8 w-auto text-xs">
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
            <select
              className="input h-8 w-auto text-xs"
              defaultValue=""
              onChange={(e) => {
                const v = views.find((x) => x.id === e.target.value);
                if (v) applyView(v);
                e.target.value = '';
              }}
            >
              <option value="">Vistas…</option>
              {views.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <button type="button" onClick={saveCurrentView} className="btn-ghost h-8 text-xs">Guardar vista</button>
          </div>
        )}
      </div>

      {/* BULK ACTION BAR */}
      {writable && selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="surface p-3 mb-3 flex flex-wrap items-center gap-2 sticky top-0 z-20 border-2 border-primary/40">
          <span className="text-sm font-medium">{selected.size} seleccionado{selected.size === 1 ? '' : 's'}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <button data-testid="bulk-whatsapp-open" onClick={() => setBulkWhatsAppOpen(true)} className="btn-ghost">
            <MessageCircle className="h-4 w-4" />
            Enviar WhatsApp
          </button>
          <button data-testid="bulk-email-open" onClick={() => setBulkEmailOpen(true)} className="btn-ghost">
            <Send className="h-4 w-4" />
            Enviar email
          </button>
          {leadLists.length > 0 && (
            <div className="flex items-center gap-1">
              <select value={bulkListTo} onChange={(e) => setBulkListTo(e.target.value)} className="input h-9 text-xs w-auto">
                <option value="">Agregar a lista…</option>
                {leadLists.map((lst) => <option key={lst.id} value={lst.id}>{lst.name}</option>)}
              </select>
              <button
                type="button"
                className="btn-ghost h-9 text-xs"
                disabled={!bulkListTo || bulkBusy}
                onClick={async () => {
                  if (!bulkListTo) return;
                  setBulkBusy(true);
                  try {
                    const { data } = await api.post(`/lead-lists/${bulkListTo}/members`, {
                      lead_ids: [...selected],
                    });
                    toast.success(`${data.modified ?? selected.size} agregados a la lista`);
                    setBulkListTo('');
                    clearSelection();
                    load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Error');
                  } finally {
                    setBulkBusy(false);
                  }
                }}
              >
                Agregar
              </button>
              {listId && (
                <button
                  type="button"
                  className="btn-ghost h-9 text-xs text-[hsl(var(--danger))]"
                  disabled={bulkBusy}
                  onClick={async () => {
                    setBulkBusy(true);
                    try {
                      await api.delete(`/lead-lists/${listId}/members`, {
                        data: { lead_ids: [...selected] },
                      });
                      toast.success('Quitados de esta lista');
                      clearSelection();
                      load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Error');
                    } finally {
                      setBulkBusy(false);
                    }
                  }}
                >
                  Quitar de lista
                </button>
              )}
            </div>
          )}
          <div className="flex items-center gap-1">
            <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              data-testid="bulk-assign-select"
              value={bulkAssignTo}
              onChange={(e) => setBulkAssignTo(e.target.value)}
              className="input h-9 w-auto text-xs"
            >
              <option value="">Sin asignar</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
            <button
              data-testid="bulk-assign-btn"
              onClick={bulkAssign}
              disabled={bulkBusy}
              className="btn-ghost h-9 text-xs"
            >
              Asignar
            </button>
          </div>
          <div className="flex items-center gap-1">
            <Layers className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              data-testid="bulk-stage-select"
              value={bulkStageTo}
              onChange={(e) => setBulkStageTo(e.target.value)}
              className="input h-9 w-auto text-xs"
            >
              <option value="">Etapa…</option>
              {stages.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <button
              data-testid="bulk-stage-btn"
              onClick={bulkStage}
              disabled={bulkBusy || !bulkStageTo}
              className="btn-ghost h-9 text-xs"
            >
              Mover
            </button>
          </div>
          <button
            data-testid="bulk-delete-btn"
            onClick={bulkDelete}
            disabled={bulkDeleting}
            className="btn-ghost text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10"
          >
            <Trash2 className="h-4 w-4" />
            {bulkDeleting ? 'Eliminando…' : 'Eliminar'}
          </button>
          <button onClick={clearSelection} className="btn-ghost ml-auto text-xs">
            <X className="h-3 w-3" />
            Limpiar selección
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex-1 min-h-0"><LoadingShell /></div>
      ) : leads.length === 0 ? (
        <div className="flex-1 min-h-0 p-6">
          <EmptyState icon={Users} title="Sin leads" description="Crea o importa un CSV." cta={<button onClick={() => setOpen(true)} className="btn-primary"><Plus className="h-4 w-4" /> Nuevo</button>} />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto bg-card border-t border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-card border-b border-border">
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="px-3 py-2 font-medium w-8">
                  <input
                    type="checkbox"
                    data-testid="leads-select-all"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAll}
                    aria-label="Seleccionar todos"
                  />
                </th>
                {tableSysFinal.map((sf) => (
                  <th key={sf.key} className="px-3 py-2 font-medium">{sf.label}</th>
                ))}
                <th className="px-3 py-2 font-medium">Etapa</th>
                <th className="px-3 py-2 font-medium">Agente</th>
                {tableCustom.map((cf) => <th key={cf.id} className="px-3 py-2 font-medium">{cf.label}</th>)}
                <th className="px-3 py-2 font-medium text-right">Última act.</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr
                  key={l.id}
                  data-testid="leads-row"
                  className={`h-10 border-b border-border/70 hover:bg-accent/60 cursor-pointer ${selected.has(l.id) ? 'bg-primary/5' : ''}`}
                  onClick={() => navigate(`/app/leads/${l.id}`)}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      data-testid={`lead-select-${l.id}`}
                      checked={selected.has(l.id)}
                      onChange={() => toggleSelect(l.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Seleccionar ${l.first_name} ${l.last_name}`}
                    />
                  </td>
                  {tableSysFinal.map((sf) => (
                    <td key={sf.key} className="px-3 py-2">{renderSysCell(l, sf)}</td>
                  ))}
                  <td className="px-3 py-2">
                    {stageMap[l.stage_id] ? (
                      <span className="inline-flex items-center gap-1 chip" style={{ borderColor: stageMap[l.stage_id].color + '66' }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: stageMap[l.stage_id].color }} />
                        {stageMap[l.stage_id].name}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{userMap[l.assigned_to]?.name || userMap[l.assigned_to]?.email || 'Sin asignar'}</td>
                  {tableCustom.map((cf) => (
                    <td key={cf.id} className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[200px]">{formatCustomVal((l.custom_values || {})[cf.key], cf.type)}</td>
                  ))}
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground">{fmtRelative(l.last_activity_at || l.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-3 py-3 border-t border-border text-sm">
            <span className="text-muted-foreground">Página {page} de {totalPages} · {total} leads</span>
            <div className="flex items-center gap-2">
              <button className="btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</button>
              <button className="btn-ghost" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Siguiente</button>
            </div>
          </div>
        </div>
      )}
      <LeadCreateModal open={open} onClose={() => setOpen(false)} onCreated={() => load()} customFields={customFields} users={users} sysFields={formSys} />
      <BulkWhatsAppModal
        open={bulkWhatsAppOpen}
        onClose={() => setBulkWhatsAppOpen(false)}
        leadIds={[...selected]}
        templates={waTemplates}
        onSent={() => { clearSelection(); load(); }}
      />
      <BulkEmailModal
        open={bulkEmailOpen}
        onClose={() => setBulkEmailOpen(false)}
        leadIds={[...selected]}
        emailTemplates={emailTemplates}
        onSent={() => { clearSelection(); load(); }}
      />
    </div>
  );
}
